// Extracted verbatim from app.js. State is scoped to one compiler instance.
const SHAPER_MODEL_ID = 'gemini-3.5-flash';
const PROMPT_FORMAT_VERSION = '3.0';
const PLAN_FORMAT_VERSION = '1.0';
const MAX_INPUT_IMAGES = 14;
// The image model accepts fixed size tiers only (512px, 1K, 2K, 4K).
const IMAGE_SIZE = '1K';
const SHAPER_ROLES = Object.freeze(new Set([
    'hero', 'benefit', 'feature-detail', 'material-detail', 'scale', 'usage',
    'alternate-view', 'package-contents', 'lifestyle'
]));

const PLATFORM_TEMPLATES = {
    'amazon-jp': {
        name: 'Amazon.co.jp',
        imageCount: 7,
        minImageCount: 1,
        maxImageCount: 10,
        aspectRatio: '1:1',
        imagePurposes: [
            'Main product image',
            'Primary feature detail',
            'Secondary feature detail',
            'Lifestyle context',
            'Size and scale',
            'Product in use',
            'Package contents'
        ],
        tone: 'Clean, trustworthy, accurate product photography with restrained styling.',
        slotRules: [
            'Show only the actual product for sale on a pure white #FFFFFF background. Center it, keep it fully visible, let it fill about 85% of the frame, and include no added text, graphics, watermarks, borders, props, or accessories not included with the product.',
            'Show one supplied product feature clearly in a close detail view. Keep the product recognizable and avoid unsupported labels or claims.',
            'Show a different supplied product feature clearly. Use a distinct composition from the previous image.',
            'Place the product in a believable usage setting with only contextually appropriate props. Do not imply items are included unless supplied as product facts.',
            'Communicate scale using a believable environment or familiar neutral reference. Do not invent measurements; reserve space for exact supplied dimensions when needed.',
            'Demonstrate a realistic use case without changing the product or implying unsupported performance.',
            'Show only packaging, accessories, and components confirmed by the supplied product facts and photos.'
        ],
        constraints: [
            { id: 'key_features', label: 'Key Features (3-5 points)', type: 'textarea', defaultLevel: 'preferred' },
            { id: 'exact_claims', label: 'Approved Claims', type: 'textarea', defaultLevel: 'locked' },
            { id: 'brand_logo', label: 'Brand Treatment', type: 'text', defaultLevel: 'preferred' },
            { id: 'safety_text', label: 'Safety/Legal Text', type: 'textarea', defaultLevel: 'locked' },
            { id: 'background_style', label: 'Preferred Background Style', type: 'text', defaultLevel: 'preferred' },
            { id: 'props', label: 'Preferred Props/Context', type: 'text', defaultLevel: 'preferred' }
        ]
    },
    'shopee-tw': {
        name: 'Shopee TW',
        imageCount: 9,
        minImageCount: 1,
        maxImageCount: 10,
        aspectRatio: '1:1',
        imagePurposes: [
            'Promotional hero',
            'Primary customer benefit',
            'Primary feature',
            'Secondary feature',
            'Material or finish detail',
            'Size and scale',
            'Package contents',
            'Alternate product view',
            'Lifestyle usage scene'
        ],
        tone: 'Clear, energetic eCommerce imagery with strong product visibility and an original promotional composition.',
        slotRules: [
            'Use a clean cover image: make the product the unmistakable focal point, keep it fully visible, and render no added text, price, badge, watermark, or promotional graphic.',
            'Visualize one supplied customer benefit without inventing performance or before-and-after results.',
            'Communicate one supplied feature through the product itself, a detail view, or a simple visual demonstration.',
            'Communicate a different supplied feature using a new composition rather than repeating the previous layout.',
            'Reveal the real material, surface, texture, or finish accurately from the product references.',
            'Communicate scale without inventing dimensions; reserve space for exact supplied measurements when needed.',
            'Show only the bundle, packaging, and accessories confirmed by supplied facts and photos.',
            'Use a fresh camera angle or crop that reveals another useful part of the actual product.',
            'Show a believable use moment for the target customer without changing the product or making unsupported claims.'
        ],
        constraints: [
            { id: 'promotional_price', label: 'Price & Discount Info', type: 'text', defaultLevel: 'locked' },
            { id: 'key_selling_points', label: 'Key Selling Points', type: 'textarea', defaultLevel: 'locked' },
            { id: 'promotional_message', label: 'Short Promotional Headline', type: 'text', defaultLevel: 'preferred' },
            { id: 'brand_colors', label: 'Brand Colors (hex codes)', type: 'text', defaultLevel: 'preferred' },
            { id: 'lifestyle_context', label: 'Lifestyle Scene Context', type: 'text', defaultLevel: 'preferred' },
            { id: 'safety_text', label: 'Safety/Legal Text', type: 'textarea', defaultLevel: 'locked' },
            { id: 'background_props', label: 'Preferred Background Props', type: 'text', defaultLevel: 'preferred' }
        ]
    },
    'rakuten': {
        name: 'Rakuten',
        imageCount: 7,
        minImageCount: 1,
        maxImageCount: 10,
        aspectRatio: '1:1',
        imagePurposes: [
            'Main product image',
            'Primary feature',
            'Quality and material detail',
            'Size and scale',
            'Secondary feature',
            'Typical use environment',
            'Packaging and included accessories'
        ],
        tone: 'Detailed, reliable product photography with accurate color and an informative but uncluttered presentation.',
        slotRules: [
            'Present the complete product clearly against a clean, simple background. Keep added decoration restrained.',
            'Show one supplied feature clearly without adding unsupported specifications or claims.',
            'Use a close view to communicate the actual material, construction, or finish seen in the references.',
            'Communicate scale without inventing dimensions; reserve space for exact supplied measurements when needed.',
            'Show a different supplied feature with an original layout and useful product angle.',
            'Place the product in a believable typical-use environment without implying unsupported capabilities.',
            'Show only packaging, accessories, and components confirmed by supplied facts and photos.'
        ],
        constraints: [
            { id: 'specifications', label: 'Product Specifications', type: 'textarea', defaultLevel: 'locked' },
            { id: 'quality_claims', label: 'Approved Quality/Material Claims', type: 'textarea', defaultLevel: 'locked' },
            { id: 'size_info', label: 'Size/Dimension Information', type: 'text', defaultLevel: 'preferred' },
            { id: 'trust_markers', label: 'Trust Markers (warranty, certification)', type: 'text', defaultLevel: 'preferred' },
            { id: 'brand_identity', label: 'Brand Treatment', type: 'text', defaultLevel: 'preferred' },
            { id: 'safety_text', label: 'Safety/Legal Text', type: 'textarea', defaultLevel: 'locked' }
        ]
    }
};

// Platform locale defaults for generated asset copy. This is separate from the
// application's UI locale and from the market profile used for visual context.
const PLATFORM_LOCALES = {
    'amazon-jp': { locale: 'ja-JP', languageName: 'Japanese', direction: 'ltr' },
    rakuten: { locale: 'ja-JP', languageName: 'Japanese', direction: 'ltr' },
    'shopee-tw': { locale: 'zh-TW', languageName: 'Traditional Chinese', direction: 'ltr' },
    'qoo10-jp': { locale: 'ja-JP', languageName: 'Japanese', direction: 'ltr' }
};

// Image-copy strategy is separate from the UI locale and from the market
// profile. These are operational guardrails for Shaper and image prompts.
const PLATFORM_COPY_POLICIES = {
    'amazon-jp': {
        mode: 'main-image-restricted',
        allowShaperAuthoredCopy: true,
        firstSlotTextFree: true,
        maxItemsPerSlot: 1,
        maxCharactersPerItem: 32,
        guidance: 'Keep the primary image text-free. For secondary images, Shaper may author at most one concise factual Japanese feature, comparison, or usage label from supplied product facts and approved constraints. Never add prices, badges, reviews, unsupported claims, disclaimers, or safety text to an image.'
    },
    'shopee-tw': {
        mode: 'image-led',
        allowShaperAuthoredCopy: true,
        maxItemsPerSlot: 2,
        maxCharactersPerItem: 32,
        guidance: 'Treat later preview images as an important product-information surface. Keep the cover image text-free; for later slots, Shaper may author one or two concise Traditional Chinese slogan, selling-point, feature, or bundle labels from supplied product facts and approved constraints. Keep disclaimers, safety, exclusions, and legal text in listing descriptions unless the operator explicitly supplies them as approved overlay copy.'
    },
    rakuten: {
        mode: 'limited',
        allowShaperAuthoredCopy: true,
        maxItemsPerSlot: 2,
        maxCharactersPerItem: 32,
        firstImageTextAreaLimit: '20%',
        guidance: 'Use concise Japanese product names, feature labels, or catchphrases where they improve comprehension. Keep text on the first product image and SKU image within 20% of the image area; avoid dense promotional panels. Keep legal, safety, and disclaimer text in listing content unless explicitly approved.'
    }
};

function getPlatformCopyPolicy(platformOrTemplate) {
    const platformId = typeof platformOrTemplate === 'string'
        ? platformOrTemplate
        : Object.entries(PLATFORM_TEMPLATES).find(([, template]) => template === platformOrTemplate)?.[0];
    return PLATFORM_COPY_POLICIES[platformId] || {
        mode: 'limited',
        allowShaperAuthoredCopy: false,
        maxItemsPerSlot: 0,
        guidance: 'Use only operator-approved copy and keep legal or safety information in listing content.'
    };
}

function getLocaleFromPlatform(platformId) {
    return PLATFORM_LOCALES[platformId]?.locale || 'en-US';
}

function localizeCopyItems(items, locale) {
    return (items || []).map(item => {
        const localizedText = item?.textByLocale?.[locale];
        const hasLocalizedText = typeof localizedText === 'string' && localizedText.trim();
        const text = hasLocalizedText
            ? localizedText.trim()
            : String(item?.text || '').trim();
        const sourceLocale = item?.sourceLocale || item?.locale || locale;
        return {
            ...item,
            sourceLocale,
            targetLocale: locale,
            locale: hasLocalizedText ? locale : sourceLocale,
            text
        };
    }).filter(item => item.text);
}

const CATEGORY_PRESETS = {
    beauty: {
        name: 'Beauty & Personal Care',
        guidance: 'Preserve packaging shape, cap or dispenser, label placement, shade, product texture, and finish. Show application only when supported by the supplied product facts. Do not invent cosmetic, medical, or performance claims.'
    },
    electronics: {
        name: 'Electronics & Appliances',
        guidance: 'Preserve geometry, controls, ports, displays, materials, and supplied accessories. Keep screens and indicators believable. Do not invent connectivity, capacity, compatibility, or performance.'
    },
    apparel: {
        name: 'Apparel & Accessories',
        guidance: 'Preserve garment or accessory color, pattern, fabric appearance, construction, hardware, and silhouette. Keep fit and drape believable and do not invent unseen details.'
    },
    food: {
        name: 'Food & Beverage',
        guidance: 'Preserve packaging, label, quantity, color, and product appearance. Treat serving scenes as suggestions, distinguish props from included items, and do not invent ingredients, nutrition, origin, or health claims.'
    },
    home: {
        name: 'Home & Living',
        guidance: 'Preserve form, material, finish, construction, dimensions when supplied, and included components. Keep room scale believable and make contextual props clearly separate from the product.'
    }
};

// Market Cultural Context profiles based on Domain 4 of the academic framework.
// These are engineering priors for stimulus generation, not validated findings.
const MARKET_PROFILES = {
    japan: {
        name: 'Japan Market',
        contextualImageryPreference: 'high',
        informationDensityTolerance: 'low',
        trustEvidenceRequirement: 'medium',
        socialProofSensitivity: 'low',
        promotionSensitivity: 'low',
        lifestyleIdentification: 'high',
        visualTendencies: {
            backgroundPreference: 'contextual scene or gradient',
            colorPalette: 'natural, muted, warm tones',
            textIntegration: 'minimal or reserved',
            productPresentation: 'in-context when appropriate'
        }
    },
    taiwan: {
        name: 'Taiwan Market',
        contextualImageryPreference: 'medium',
        informationDensityTolerance: 'high',
        trustEvidenceRequirement: 'high',
        socialProofSensitivity: 'high',
        promotionSensitivity: 'high',
        lifestyleIdentification: 'medium',
        visualTendencies: {
            backgroundPreference: 'vibrant or promotional',
            colorPalette: 'energetic, saturated colors',
            textIntegration: 'overlay-friendly',
            productPresentation: 'clear product visibility'
        }
    },
    hongkong: {
        name: 'Hong Kong Market',
        contextualImageryPreference: 'medium',
        informationDensityTolerance: 'medium',
        trustEvidenceRequirement: 'high',
        socialProofSensitivity: 'medium',
        promotionSensitivity: 'medium',
        lifestyleIdentification: 'medium',
        visualTendencies: {
            backgroundPreference: 'clean or subtle context',
            colorPalette: 'modern, professional tones',
            textIntegration: 'balanced',
            productPresentation: 'clear + quality signals'
        }
    },
    china: {
        name: 'Mainland China Market',
        contextualImageryPreference: 'medium-high',
        informationDensityTolerance: 'very-high',
        trustEvidenceRequirement: 'very-high',
        socialProofSensitivity: 'very-high',
        promotionSensitivity: 'very-high',
        lifestyleIdentification: 'high',
        visualTendencies: {
            backgroundPreference: 'rich, detailed scenes',
            colorPalette: 'bold, premium, gold accents',
            textIntegration: 'heavy overlay acceptable',
            productPresentation: 'aspirational + detailed'
        }
    }
};

// Map platform IDs to market IDs.
function getMarketFromPlatform(platformId) {
    const marketMap = {
        'amazon-jp': 'japan',
        rakuten: 'japan',
        'shopee-tw': 'taiwan',
        'qoo10-jp': 'japan'
    };
    return marketMap[platformId] || 'japan';
}

const OVERLAY_CONSTRAINT_IDS = new Set([
    'promotional_price',
    'safety_text',
    'specifications',
    'quality_claims',
    'size_info',
    'trust_markers',
    'exact_claims'
]);


const BASE_TEMPLATES = PLATFORM_TEMPLATES;
const baseCopyPolicy = getPlatformCopyPolicy;

export function createLegacyCompiler(input, template) {
 const state = input;
 const PLATFORM_TEMPLATES = { ...BASE_TEMPLATES, [input.platform]: template || BASE_TEMPLATES[input.platform] };
 const getPlatformCopyPolicy = () => baseCopyPolicy(input.platform);
 const generateId = (prefix) => `${prefix}-${crypto.randomUUID()}`;
function getActiveConstraints(template) {
    return template.constraints.flatMap(definition => {
        const value = state.constraints[definition.id];
        if (!value?.enabled || !value.value?.trim()) return [];

        return [{
            id: definition.id,
            label: definition.label,
            level: value.level === 'locked' ? 'must-have' : 'preferred',
            value: value.value.trim()
        }];
    });
}

function getSelectedAssets() {
    const productImages = state.productImages.slice(0, MAX_INPUT_IMAGES);
    const remainingSlots = Math.max(0, MAX_INPUT_IMAGES - productImages.length);
    const referenceImages = (state.referenceImages || []).slice(0, remainingSlots);

    return { productImages, referenceImages };
}

function buildCopyPlan(constraints, imageIndex) {
    const shapedPlacement = state.shaperPlan?.planSource === 'shaper'
        ? state.shaperPlan.slots?.[imageIndex]?.copyPlacement
        : null;
    if (shapedPlacement) {
        const reserveSlots = state.shaperPlan.slots
            .map((slot, index) => slot.copyPlacement === 'reserve-overlay-area' ? index : -1)
            .filter(index => index >= 0);
        const overlayItems = constraints.filter(item => OVERLAY_CONSTRAINT_IDS.has(item.id));
        const overlayText = shapedPlacement === 'reserve-overlay-area'
            ? overlayItems.filter((_, index) => reserveSlots[index % reserveSlots.length] === imageIndex)
            : [];
        const headline = constraints.find(item => item.id === 'promotional_message');
        const modelRenderedText = shapedPlacement === 'model-rendered' && headline && state.platform !== 'amazon-jp'
            ? [{ label: headline.label, value: headline.value }]
            : [];
        return { modelRenderedText, overlayText };
    }
    const lastSlot = Math.max(0, state.imageCount - 1);
    const copySlot = {
        promotional_price: 0,
        trust_markers: 0,
        specifications: Math.min(1, lastSlot),
        exact_claims: Math.min(1, lastSlot),
        quality_claims: Math.min(2, lastSlot),
        size_info: Math.min(3, lastSlot),
        safety_text: lastSlot
    };

    const overlayText = constraints.filter(item => (
        OVERLAY_CONSTRAINT_IDS.has(item.id) && copySlot[item.id] === imageIndex
    ));
    const headline = constraints.find(item => item.id === 'promotional_message');
    const modelRenderedText = headline && imageIndex === 0 && state.platform !== 'amazon-jp'
        ? [{ label: headline.label, value: headline.value }]
        : [];

    return { modelRenderedText, overlayText };
}

function roleForPurpose(purpose, index) {
    const text = String(purpose || '').toLowerCase();
    if (text.includes('hero') || text.includes('main product')) return 'hero';
    if (text.includes('benefit')) return 'benefit';
    if (text.includes('material') || text.includes('quality') || text.includes('finish')) return 'material-detail';
    if (text.includes('scale') || text.includes('size')) return 'scale';
    if (text.includes('package') || text.includes('packaging')) return 'package-contents';
    if (text.includes('lifestyle') || text.includes('environment') || text.includes('context')) return 'lifestyle';
    if (text.includes('use')) return 'usage';
    if (text.includes('alternate')) return 'alternate-view';
    if (text.includes('feature')) return 'feature-detail';
    return index === 0 ? 'hero' : 'feature-detail';
}

function fallbackCopyPlacement(template, index, count) {
    const copyPolicy = getPlatformCopyPolicy(template);
    if (copyPolicy.firstSlotTextFree && index === 0) return 'none';
    const lastSlot = Math.max(0, count - 1);
    const copySlot = {
        promotional_price: 0, trust_markers: 0, specifications: Math.min(1, lastSlot),
        exact_claims: Math.min(1, lastSlot), quality_claims: Math.min(2, lastSlot),
        size_info: Math.min(3, lastSlot), safety_text: lastSlot
    };
    const ids = Object.keys(copySlot).filter(id => copySlot[id] === index);
    if (ids.length) return 'reserve-overlay-area';
    if (copyPolicy.mode === 'image-led' && index > 0) return 'model-rendered';
    return index === 0 && !copyPolicy.firstSlotTextFree ? 'model-rendered' : (index > 0 && copyPolicy.mode === 'main-image-restricted' ? 'model-rendered' : 'none');
}

function fallbackVisualElements(role, index, market) {
    const copyPolicy = getPlatformCopyPolicy(state.platform);
    const isLifestyle = role === 'lifestyle' || role === 'usage';
    const isDetail = role === 'material-detail' || role === 'feature-detail' || role === 'scale';
    const isTaiwanOrChina = market === 'taiwan' || market === 'china';
    return {
        backgroundType: isLifestyle ? 'contextual-scene' : (index === 0 ? 'pure-white' : 'neutral-solid'),
        productTreatment: isLifestyle ? 'in-context' : (isDetail ? 'angled-with-shadow' : 'centered-isolated'),
        layoutComposition: isLifestyle ? 'environmental' : (index === 0 ? 'product-dominant' : 'balanced'),
        colorPalette: isTaiwanOrChina && index > 0 ? 'vibrant-pop' : 'product-accurate',
        lifestyleLevel: isLifestyle ? 'full-scene' : (isDetail ? 'subtle-props' : 'none'),
        textStrategy: (copyPolicy.mode === 'image-led' || copyPolicy.mode === 'main-image-restricted') && index > 0
            ? 'model-rendered-headline'
            : (copyPolicy.mode === 'limited' && index === 0 ? 'reserve-overlay-space' : 'text-free')
    };
}

function buildFallbackPlan(template = PLATFORM_TEMPLATES[state.platform]) {
    const count = Math.max(template.minImageCount, Math.min(state.imageCount || template.imageCount, template.maxImageCount));
    const outputLocale = /^[a-z]{2,3}-[A-Z]{2}$/.test(state.locale || '')
        ? state.locale
        : getLocaleFromPlatform(state.platform);
    const instructionLanguage = state.instructionLanguage === 'localized' ? 'localized' : 'en';
    const copyItems = localizeCopyItems(state.copyItems, outputLocale);
    const slots = Array.from({ length: count }, (_, index) => ({
        index: index + 1,
        role: roleForPurpose(template.imagePurposes[index], index),
        direction: template.slotRules[index] || 'Create a useful additional product view with a new composition that fits the shared batch tone.',
        differentiator: template.imagePurposes[index] || `Additional product view ${index + 1}`,
        sceneRationale: 'Static platform guidance; use a plain product view unless the platform rule calls for context.',
        sceneSource: 'operator',
        copyPlacement: fallbackCopyPlacement(template, index, count),
        outputLocale,
        instructionLanguage,
        copyItems: copyItems.map(item => ({ ...item })),
        visualElements: fallbackVisualElements(roleForPurpose(template.imagePurposes[index], index), index, state.market),
        derivedFrom: 'platform-rule'
    }));
    return {
        planFormatVersion: PLAN_FORMAT_VERSION,
        id: generateId('plan'),
        planSource: 'fallback',
        shapedAt: new Date().toISOString(),
        model: SHAPER_MODEL_ID,
        productRead: {
            verificationNeed: 'medium',
            purchaseType: 'one-off',
            infoLocation: 'both',
            anglesSupplied: state.productImages?.length || 0,
            notes: 'Fallback to platform template.',
            buyerMotivation: {
                primary: 'B1_Functional',
                secondary: [],
                confidence: 0.5,
                reason: 'Fallback to platform template.'
            }
        },
        batchTone: { character: template.tone, palette: 'Accurate product colors', mood: 'Trustworthy', finish: 'Polished product photography' },
        resolvedImageCount: count,
        countRationale: 'Use the platform template count.',
        slots
    };
}

function parseShaperResponse(raw) {
    if (raw && typeof raw === 'object') return raw;
    const text = typeof raw === 'string' ? raw : '';
    const unfenced = text.replace(/^\s*```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
    try { return JSON.parse(unfenced); } catch (_) { return null; }
}

function validateRawShaperPlan(raw, template = PLATFORM_TEMPLATES[state.platform]) {
    const parsed = parseShaperResponse(raw);
    const issues = [];
    const fallback = buildFallbackPlan(template);
    const expectedCount = state.imageCountTouched
        ? Math.max(template.minImageCount, Math.min(Number(state.imageCount) || template.imageCount, template.maxImageCount))
        : Math.max(template.minImageCount, Math.min(Number(parsed?.resolvedImageCount) || template.imageCount, template.maxImageCount));
    const addIssue = (path, message) => issues.push(`${path}: ${message}`);
    const requiredObject = (value, path, fields) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            addIssue(path, 'must be an object');
            return false;
        }
        fields.forEach(field => {
            if (value[field] === undefined || value[field] === null) addIssue(`${path}.${field}`, 'is required');
        });
        return true;
    };

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        addIssue('response', 'must be a JSON object');
        return { valid: false, issues, parsed: null, expectedSlotCount: expectedCount };
    }
    ['planFormatVersion', 'id', 'planSource', 'shapedAt', 'model', 'productRead', 'batchTone', 'resolvedImageCount', 'countRationale', 'slots']
        .forEach(field => {
            if (parsed[field] === undefined || parsed[field] === null) addIssue(field, 'is required');
        });
    if (parsed.planFormatVersion !== PLAN_FORMAT_VERSION) addIssue('planFormatVersion', `must be ${PLAN_FORMAT_VERSION}`);
    if (parsed.planSource !== 'shaper') addIssue('planSource', 'must be shaper');
    if (parsed.model !== SHAPER_MODEL_ID) addIssue('model', `must be ${SHAPER_MODEL_ID}`);
    if (parsed.resolvedImageCount !== expectedCount) addIssue('resolvedImageCount', `must equal ${expectedCount}`);
    requiredObject(parsed.productRead, 'productRead', ['verificationNeed', 'purchaseType', 'infoLocation', 'anglesSupplied', 'notes', 'buyerMotivation']);
    requiredObject(parsed.batchTone, 'batchTone', ['character', 'palette', 'mood', 'finish']);
    if (parsed.productRead) {
        if (!['low', 'medium', 'high'].includes(parsed.productRead.verificationNeed)) addIssue('productRead.verificationNeed', 'has an invalid value');
        if (!['repeat', 'one-off'].includes(parsed.productRead.purchaseType)) addIssue('productRead.purchaseType', 'has an invalid value');
        if (!['packaging', 'listing', 'both'].includes(parsed.productRead.infoLocation)) addIssue('productRead.infoLocation', 'has an invalid value');
        const motivation = parsed.productRead.buyerMotivation;
        if (requiredObject(motivation, 'productRead.buyerMotivation', ['primary', 'confidence'])) {
            const codes = ['B1_Functional', 'B2_Evidence', 'B3_Lifestyle', 'B4_Aesthetic', 'B5_Value', 'B6_Convenience', 'B7_Expert'];
            if (!codes.includes(motivation.primary)) addIssue('productRead.buyerMotivation.primary', 'has an invalid value');
            if (typeof motivation.confidence !== 'number' || motivation.confidence < 0 || motivation.confidence > 1) addIssue('productRead.buyerMotivation.confidence', 'must be a number from 0 to 1');
        }
    }
    if (!Array.isArray(parsed.slots)) {
        addIssue('slots', 'must be an array');
        return { valid: false, issues, parsed, expectedSlotCount: expectedCount };
    }
    if (parsed.slots.length !== expectedCount) addIssue('slots', `must contain exactly ${expectedCount} items`);
    const indexes = new Set();
    const validRoles = SHAPER_ROLES;
    const copyPolicy = getPlatformCopyPolicy(template);
    const visualEnums = {
        backgroundType: ['pure-white', 'neutral-solid', 'gradient', 'contextual-scene', 'lifestyle-environment'],
        productTreatment: ['centered-isolated', 'angled-with-shadow', 'in-context', 'in-use'],
        layoutComposition: ['product-dominant', 'balanced', 'environmental'],
        colorPalette: ['product-accurate', 'warm-enhanced', 'cool-enhanced', 'vibrant-pop'],
        lifestyleLevel: ['none', 'subtle-props', 'full-scene', 'human-presence'],
        textStrategy: ['text-free', 'reserve-overlay-space', 'model-rendered-headline']
    };
    const copyKinds = ['slogan', 'short-description', 'badge', 'label'];
    const copyLocations = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right', 'custom'];
    parsed.slots.forEach((slot, position) => {
        const path = `slots[${position}]`;
        if (!slot || typeof slot !== 'object' || Array.isArray(slot)) {
            addIssue(path, 'must be an object');
            return;
        }
        ['index', 'role', 'direction', 'differentiator', 'sceneRationale', 'sceneSource', 'copyPlacement', 'outputLocale', 'instructionLanguage', 'copyItems', 'derivedFrom', 'visualElements']
            .forEach(field => { if (slot[field] === undefined || slot[field] === null) addIssue(`${path}.${field}`, 'is required'); });
        if (!Number.isInteger(slot.index) || slot.index < 1 || slot.index > expectedCount) addIssue(`${path}.index`, `must be an integer from 1 to ${expectedCount}`);
        else if (indexes.has(slot.index)) addIssue(`${path}.index`, 'must be unique');
        else indexes.add(slot.index);
        if (!validRoles.has(slot.role)) addIssue(`${path}.role`, 'has an invalid value');
        ['direction', 'differentiator', 'sceneRationale', 'outputLocale'].forEach(field => { if (typeof slot[field] !== 'string' || !slot[field].trim()) addIssue(`${path}.${field}`, 'must be a non-empty string'); });
        if (!['shaper', 'operator'].includes(slot.sceneSource)) addIssue(`${path}.sceneSource`, 'has an invalid value');
        if (!['none', 'model-rendered', 'reserve-overlay-area'].includes(slot.copyPlacement)) addIssue(`${path}.copyPlacement`, 'has an invalid value');
        if (!['en', 'localized'].includes(slot.instructionLanguage)) addIssue(`${path}.instructionLanguage`, 'has an invalid value');
        if (!['open', 'platform-rule', 'operator'].includes(slot.derivedFrom)) addIssue(`${path}.derivedFrom`, 'has an invalid value');
        const fallbackSlot = fallback.slots[slot.index - 1];
        if (fallbackSlot && slot.outputLocale !== fallbackSlot.outputLocale) addIssue(`${path}.outputLocale`, `must equal ${fallbackSlot.outputLocale}`);
        if (!Array.isArray(slot.copyItems)) {
            addIssue(`${path}.copyItems`, 'must be an array');
        } else {
            const maxItems = copyPolicy.firstSlotTextFree && slot.index === 1 ? 0 : copyPolicy.maxItemsPerSlot;
            if (slot.copyItems.length > maxItems) {
                addIssue(`${path}.copyItems`, `may contain at most ${maxItems} item(s) for this platform`);
            }
            const approved = new Map((fallbackSlot?.copyItems || []).map(item => [item.id, item]));
            const seenCopy = new Set();
            slot.copyItems.forEach((item, copyIndex) => {
                const copyPath = `${path}.copyItems[${copyIndex}]`;
                if (!item || typeof item !== 'object' || Array.isArray(item)) { addIssue(copyPath, 'must be an object'); return; }
                if (Object.hasOwn(item, 'textByLocale')) addIssue(`${copyPath}.textByLocale`, 'must remain in operator input');
                ['id', 'kind', 'text', 'locale', 'location', 'render'].forEach(field => { if (item[field] === undefined || item[field] === null) addIssue(`${copyPath}.${field}`, 'is required'); });
                const expected = approved.get(item.id);
                const shaperAuthored = copyPolicy.allowShaperAuthoredCopy
                    && typeof item.id === 'string'
                    && item.id.startsWith('shaper-');
                if (!expected && !shaperAuthored) addIssue(`${copyPath}.id`, 'must reference approved copy or use the platform shaper-copy prefix');
                if (seenCopy.has(item.id)) addIssue(`${copyPath}.id`, 'must be unique');
                seenCopy.add(item.id);
                if (!copyKinds.includes(item.kind)) addIssue(`${copyPath}.kind`, 'has an invalid value');
                if (typeof item.text !== 'string' || !item.text.trim()) addIssue(`${copyPath}.text`, 'must be a non-empty string');
                if (expected) {
                    ['kind', 'text', 'locale', 'location', 'render'].forEach(field => { if (item[field] !== expected[field]) addIssue(`${copyPath}.${field}`, 'must match approved copy'); });
                }
                if (shaperAuthored) {
                    if (item.locale !== fallbackSlot?.outputLocale) addIssue(`${copyPath}.locale`, `must equal ${fallbackSlot?.outputLocale}`);
                    if (item.render !== 'model-rendered') addIssue(`${copyPath}.render`, 'must be model-rendered for Shaper-authored copy');
                    if (copyPolicy.maxCharactersPerItem && Array.from(String(item.text || '')).length > copyPolicy.maxCharactersPerItem) {
                        addIssue(`${copyPath}.text`, `must be ${copyPolicy.maxCharactersPerItem} characters or fewer`);
                    }
                }
                if (!copyLocations.includes(item.location)) addIssue(`${copyPath}.location`, 'has an invalid value');
                if (!['overlay', 'model-rendered'].includes(item.render)) addIssue(`${copyPath}.render`, 'has an invalid value');
            });
        }
        const visuals = slot.visualElements;
        if (!requiredObject(visuals, `${path}.visualElements`, Object.keys(visualEnums))) return;
        Object.entries(visualEnums).forEach(([field, values]) => { if (!values.includes(visuals[field])) addIssue(`${path}.visualElements.${field}`, 'has an invalid value'); });
    });
    for (let index = 1; index <= expectedCount; index += 1) if (!indexes.has(index)) addIssue('slots.index', `is missing ${index}`);
    return { valid: issues.length === 0, issues, parsed, expectedSlotCount: expectedCount };
}

function validateShaperPlan(raw, template = PLATFORM_TEMPLATES[state.platform]) {
    const parsed = parseShaperResponse(raw);
    if (!parsed || !Array.isArray(parsed.slots)) return buildFallbackPlan(template);
    const fallback = buildFallbackPlan(template);
    const copyPolicy = getPlatformCopyPolicy(template);
    const targetCount = state.imageCountTouched
        ? Math.max(template.minImageCount, Math.min(Number(state.imageCount) || template.imageCount, template.maxImageCount))
        : Math.max(template.minImageCount, Math.min(Number(parsed.resolvedImageCount) || template.imageCount, template.maxImageCount));
    const sourceSlots = new Map(parsed.slots
        .filter(slot => Number.isInteger(slot?.index) && slot.index > 0)
        .map(slot => [slot.index, slot]));
    const slots = Array.from({ length: targetCount }, (_, index) => {
        const candidate = sourceSlots.get(index + 1);
        if (!candidate || typeof candidate !== 'object') {
            const repaired = fallback.slots[index] || fallback.slots[fallback.slots.length - 1];
            return { ...repaired, index: index + 1 };
        }
        const fallbackSlot = fallback.slots[index] || fallback.slots[fallback.slots.length - 1];
        const role = SHAPER_ROLES.has(candidate.role) ? candidate.role : fallbackSlot.role;
        const fallbackVisuals = fallbackVisualElements(role, index, state.market);
        const candidateVisuals = candidate.visualElements && typeof candidate.visualElements === 'object'
            ? candidate.visualElements
            : {};
        const visualElements = {
            backgroundType: ['pure-white', 'neutral-solid', 'gradient', 'contextual-scene', 'lifestyle-environment'].includes(candidateVisuals.backgroundType) ? candidateVisuals.backgroundType : fallbackVisuals.backgroundType,
            productTreatment: ['centered-isolated', 'angled-with-shadow', 'in-context', 'in-use'].includes(candidateVisuals.productTreatment) ? candidateVisuals.productTreatment : fallbackVisuals.productTreatment,
            layoutComposition: ['product-dominant', 'balanced', 'environmental'].includes(candidateVisuals.layoutComposition) ? candidateVisuals.layoutComposition : fallbackVisuals.layoutComposition,
            colorPalette: ['product-accurate', 'warm-enhanced', 'cool-enhanced', 'vibrant-pop'].includes(candidateVisuals.colorPalette) ? candidateVisuals.colorPalette : fallbackVisuals.colorPalette,
            lifestyleLevel: ['none', 'subtle-props', 'full-scene', 'human-presence'].includes(candidateVisuals.lifestyleLevel) ? candidateVisuals.lifestyleLevel : fallbackVisuals.lifestyleLevel,
            textStrategy: ['text-free', 'reserve-overlay-space', 'model-rendered-headline'].includes(candidateVisuals.textStrategy) ? candidateVisuals.textStrategy : fallbackVisuals.textStrategy
        };
        const outputLocale = fallbackSlot.outputLocale;
        const instructionLanguage = fallbackSlot.instructionLanguage;
        const approvedCopy = new Map(fallbackSlot.copyItems.map(item => [item.id, item]));
        const copyItems = Array.isArray(candidate.copyItems)
            ? candidate.copyItems
                .filter(item => item && (approvedCopy.has(item.id) || (
                    copyPolicy.allowShaperAuthoredCopy
                    && typeof item.id === 'string'
                    && item.id.startsWith('shaper-')
                    && !(copyPolicy.firstSlotTextFree && index === 0)
                    && item.locale === fallbackSlot.outputLocale
                    && item.render === 'model-rendered'
                    && typeof item.text === 'string'
                    && (!copyPolicy.maxCharactersPerItem || Array.from(item.text).length <= copyPolicy.maxCharactersPerItem)
                )))
                .map(item => approvedCopy.has(item.id) ? ({ ...approvedCopy.get(item.id) }) : ({
                    id: item.id,
                    kind: item.kind,
                    text: item.text,
                    locale: fallbackSlot.outputLocale,
                    location: item.location,
                    render: 'model-rendered'
                }))
            : fallbackSlot.copyItems.map(item => ({ ...item }));
        return {
            index: index + 1,
            role,
            direction: String(candidate.direction || fallbackSlot.direction),
            differentiator: String(candidate.differentiator || fallbackSlot.differentiator),
            sceneRationale: String(candidate.sceneRationale || fallbackSlot.sceneRationale),
            sceneSource: candidate.sceneSource === 'operator' ? 'operator' : 'shaper',
            copyPlacement: ['none', 'model-rendered', 'reserve-overlay-area'].includes(candidate.copyPlacement) ? candidate.copyPlacement : fallbackSlot.copyPlacement,
            outputLocale,
            instructionLanguage,
            copyItems,
            visualElements,
            derivedFrom: candidate.derivedFrom === 'platform-rule' ? 'platform-rule' : 'open'
        };
    });
    const accuracyCritical = getActiveConstraints(template).some(item => OVERLAY_CONSTRAINT_IDS.has(item.id));
    if (accuracyCritical && !slots.some(slot => slot.copyPlacement === 'reserve-overlay-area')) {
        const fallbackReserveIndex = fallback.slots.findIndex(slot => slot.copyPlacement === 'reserve-overlay-area');
        slots[Math.max(0, fallbackReserveIndex)].copyPlacement = 'reserve-overlay-area';
    }
    const seen = new Set();
    slots.forEach((slot, index) => {
        let key = slot.differentiator.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
        const nearDuplicate = Array.from(seen).some(previous => key.length > 10 && (key.includes(previous) || previous.includes(key)));
        if (!key || seen.has(key) || nearDuplicate) {
            slot.differentiator = fallback.slots[index]?.differentiator || `Distinct product view ${index + 1}`;
            key = slot.differentiator.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
        }
        seen.add(key);
    });
    const tone = parsed.batchTone && typeof parsed.batchTone === 'object' ? parsed.batchTone : fallback.batchTone;

    // Validate buyerMotivation structure
    const validMotivationCodes = ['B1_Functional', 'B2_Evidence', 'B3_Lifestyle', 'B4_Aesthetic', 'B5_Value', 'B6_Convenience', 'B7_Expert'];
    let buyerMotivation = fallback.productRead.buyerMotivation;
    if (parsed.productRead?.buyerMotivation && typeof parsed.productRead.buyerMotivation === 'object') {
        const candidate = parsed.productRead.buyerMotivation;
        if (validMotivationCodes.includes(candidate.primary) && typeof candidate.confidence === 'number') {
            buyerMotivation = {
                primary: candidate.primary,
                secondary: Array.isArray(candidate.secondary)
                    ? candidate.secondary.filter(code => validMotivationCodes.includes(code)).slice(0, 2)
                    : [],
                confidence: Math.max(0, Math.min(1, candidate.confidence)),
                reason: String(candidate.reason || 'Inferred from product images and category.')
            };
        }
    }

    return {
        planFormatVersion: PLAN_FORMAT_VERSION,
        id: String(parsed.id || generateId('plan')),
        planSource: parsed.planSource === 'fallback' ? 'fallback' : 'shaper',
        shapedAt: String(parsed.shapedAt || new Date().toISOString()),
        model: String(parsed.model || SHAPER_MODEL_ID),
        productRead: {
            verificationNeed: ['low', 'medium', 'high'].includes(parsed.productRead?.verificationNeed) ? parsed.productRead.verificationNeed : fallback.productRead.verificationNeed,
            purchaseType: ['repeat', 'one-off'].includes(parsed.productRead?.purchaseType) ? parsed.productRead.purchaseType : fallback.productRead.purchaseType,
            infoLocation: ['packaging', 'listing', 'both'].includes(parsed.productRead?.infoLocation) ? parsed.productRead.infoLocation : fallback.productRead.infoLocation,
            anglesSupplied: Math.max(0, Number.parseInt(parsed.productRead?.anglesSupplied, 10) || fallback.productRead.anglesSupplied),
            notes: String(parsed.productRead?.notes || fallback.productRead.notes),
            buyerMotivation
        },
        batchTone: {
            character: String(tone.character || fallback.batchTone.character),
            palette: String(tone.palette || fallback.batchTone.palette),
            mood: String(tone.mood || fallback.batchTone.mood),
            finish: String(tone.finish || fallback.batchTone.finish)
        },
        resolvedImageCount: targetCount,
        countRationale: String(parsed.countRationale || fallback.countRationale),
        slots
    };
}

function loadBuyerMotivationSkill() { return null; }

function buildShaperPayload(template, options = {}) {
    const assets = getSelectedAssets();
    const constraints = getActiveConstraints(template);
    const copyPolicy = getPlatformCopyPolicy(template);
    const guidanceArm = ['unguided', 'original', 'revised'].includes(options.guidanceArm)
        ? options.guidanceArm
        : 'revised';
    const marketId = MARKET_PROFILES[state.market]
        ? state.market
        : getMarketFromPlatform(state.platform);
    const marketProfile = MARKET_PROFILES[marketId] || MARKET_PROFILES.japan;
    const schema = JSON.stringify({
        planFormatVersion: PLAN_FORMAT_VERSION,
        id: 'plan_<unique id>', planSource: 'shaper', shapedAt: '<ISO timestamp>', model: SHAPER_MODEL_ID,
        productRead: {
            verificationNeed: 'low|medium|high',
            purchaseType: 'repeat|one-off',
            infoLocation: 'packaging|listing|both',
            anglesSupplied: 1,
            notes: '<short factual observation>',
            buyerMotivation: {
                primary: 'B1_Functional|B2_Evidence|B3_Lifestyle|B4_Aesthetic|B5_Value|B6_Convenience|B7_Expert',
                secondary: ['B1_Functional', '...'],
                confidence: 0.75,
                reason: '<why this motivation fits the product>'
            }
        },
        batchTone: { character: '<shared character>', palette: '<shared palette>', mood: '<shared mood>', finish: '<shared finish>' },
        resolvedImageCount: template.imageCount, countRationale: '<short reason>',
        slots: [{
            index: 1,
            role: 'hero',
            direction: '<what this slot communicates>',
            differentiator: '<how it differs from all sibling slots>',
            sceneRationale: '<why a scene or plain view is correct>',
            sceneSource: 'shaper|operator',
            copyPlacement: 'none|model-rendered|reserve-overlay-area',
            outputLocale: '<BCP-47 locale, for example ja-JP or zh-TW>',
            instructionLanguage: 'en|localized',
            copyItems: [{
                id: '<approved id or shaper-... when platform policy permits authored copy>',
                kind: 'slogan|short-description|badge|label',
                text: '<exact approved text or concise factual image copy>',
                locale: '<BCP-47 locale>',
                location: 'top-left|top-center|top-right|bottom-left|bottom-center|bottom-right|custom',
                render: 'overlay|model-rendered'
            }],
            derivedFrom: 'open|platform-rule|operator',
            visualElements: {
                backgroundType: 'pure-white|neutral-solid|gradient|contextual-scene|lifestyle-environment',
                productTreatment: 'centered-isolated|angled-with-shadow|in-context|in-use',
                layoutComposition: 'product-dominant|balanced|environmental',
                colorPalette: 'product-accurate|warm-enhanced|cool-enhanced|vibrant-pop',
                lifestyleLevel: 'none|subtle-props|full-scene|human-presence',
                textStrategy: 'text-free|reserve-overlay-space|model-rendered-headline'
            }
        }]
    }, null, 2);

    // Load buyer motivation skill from file
    const buyerMotivationSkill = loadBuyerMotivationSkill();

    const marketContextGuidance = `MARKET CULTURAL CONTEXT: ${marketProfile.name}\nMarket visual preferences:\n- Contextual imagery preference: ${marketProfile.contextualImageryPreference}\n- Information density tolerance: ${marketProfile.informationDensityTolerance}\n- Lifestyle identification: ${marketProfile.lifestyleIdentification}\nVisual tendencies: ${JSON.stringify(marketProfile.visualTendencies)}\n\nUse these market preferences as soft priors for visualElements choices per slot. They must not override product facts, platform hard rules, operator constraints, or buyer motivation. Each slot may make a different visual choice; do not force one background, palette, layout, or text strategy across the batch. The visualElements labels are shorthand categories, so keep exact colors, materials, scene details, and composition open when the evidence supports them.`;
    const revisedMarketContextGuidance = `MARKET CONTEXT HYPOTHESIS: ${marketProfile.name}\nThe following profile values are unverified hypotheses for experiment comparison, not research evidence or fixed audience facts:\n- Contextual imagery signal: ${marketProfile.contextualImageryPreference}\n- Information density signal: ${marketProfile.informationDensityTolerance}\n- Lifestyle identification signal: ${marketProfile.lifestyleIdentification}\n- Visual tendency hypotheses: ${JSON.stringify(marketProfile.visualTendencies)}\n\nUse these signals only when they are consistent with supplied product evidence, references, operator direction, platform rules, or tested market evidence. Never state that consumers in this market prefer a treatment based on this profile alone, and never use the market label as the sole reason for a slot's visual choice.`;
    const copyStrategyGuidance = `PLATFORM IMAGE-COPY STRATEGY (${copyPolicy.mode})\n${copyPolicy.guidance}\n${copyPolicy.allowShaperAuthoredCopy
        ? `When the operator has not supplied copy, author exact copyItems only for factual image communication. Use the id prefix "shaper-"; use kind slogan for a hero line, short-description for a selling point, label for a feature or bundle item, or badge only when the supplied facts support it. Use at most ${copyPolicy.maxItemsPerSlot} item(s) per slot, keep each item at ${copyPolicy.maxCharactersPerItem} characters or fewer, use outputLocale, and set render to model-rendered. Spread copy across complementary slots without repeating it.`
        : 'Do not author copyItems. Return an empty copyItems array unless an operator-approved item is supplied.'}\nDo not turn safety instructions, disclaimers, exclusions, legal text, or unsupported claims into selling copy. Every authored string must be directly supported by the product name, variant, supplied facts, product references, or locked operator constraints. Avoid prices, ratings, comparative claims, superlatives, guarantees, and performance claims unless explicitly supplied.`;
    const visualSelectionGuidance = `VISUAL ELEMENT SELECTION GUIDANCE:\nFor each slot, choose visualElements to serve the slot's communication objective. Consider, in order: platform hard rules, product evidence and category, approved operator constraints and brand direction, buyer motivation, market context, then model freedom for all remaining decisions.\n\nPlan the slots as one ordered, complementary batch story: each slot should add useful product information or context that is not already covered by its siblings. Use one shared batchTone for coherence, while allowing different backgrounds, palettes, layouts, lifestyle levels, and text strategies by slot.\n\nMarket context is a soft prior and a testable hypothesis. Do not select a treatment solely because of a country, platform, or buyer-motivation label. Do not force one background, palette, layout, lifestyle level, or text strategy across the batch. Each slot may make a different visual choice.\n\nGround rationale in this case's supplied product facts, references, operator direction, or platform rules. The market profile is an unverified hypothesis, not research evidence: do not state a market-specific consumer preference as fact based only on the market label or profile. If you use market context, identify it as a hypothesis and never make it the sole reason for a visual choice.\n\nUse pure-white only when a platform rule or strong verification objective supports it. Use contextual-scene or lifestyle-environment when the slot needs to communicate usage, scale, compatibility, or lifestyle fit. Use human-presence only when a person materially explains fit, scale, or application and the product evidence supports the depiction. Use model-rendered-headline only when approved copy exists and the experiment explicitly permits model-rendered text; otherwise use text-free or reserve-overlay-space.\n\nTreat gradient, warm-enhanced, cool-enhanced, and vibrant-pop as optional stylistic treatments. Choose them when the product, category, brand direction, supplied references, campaign, or tested market evidence supports them. Do not infer a color treatment from Japan, Taiwan, China, or any other market alone.\n\nKeep exact colors, materials, scene details, props, people, lighting, camera angle, and composition open to model judgment unless constrained by supplied facts, platform rules, or operator input. When evidence is weak, choose the least assumptive valid treatment that preserves product clarity.\n\nEvery slot must include complete visualElements, and the visualElements labels must remain a concise description of the image job rather than a complete art direction.`;

    const prompt = [
        'You are Shaper, an eCommerce image batch planner. Return JSON only, with no markdown fences.',
        `Closed roles: ${Array.from(SHAPER_ROLES).join(', ')}. Never invent a role.`,
        `JSON schema: ${schema}. Each slot must include index, role, direction, differentiator, sceneRationale, sceneSource (shaper|operator), copyPlacement (none|model-rendered|reserve-overlay-area), outputLocale, instructionLanguage, copyItems, derivedFrom (open|platform-rule|operator), and complete visualElements.`,
        'Precedence: Must Have > platform hard rule > operator Preferred > Shaper > model freedom.',
        'You may only decide what is Open. Preserve Must Have facts and platform hard rules. Do not instruct creativity, variety, imagination, or originality.',
        'Use sceneRationale to justify plain or scene-based choices. Plain slots with no scene are valid and preferred when buyer verification is high.',
        buyerMotivationSkill || 'BUYER MOTIVATION FRAMEWORK: Infer the primary buyer motivation from product images and category. Choose ONE primary from: B1_Functional (function, performance, problem-solving), B2_Evidence (specs, proof, certification), B3_Lifestyle (usage context, daily life fit), B4_Aesthetic (style, taste, brand feeling), B5_Value (price, bundle, promotion), B6_Convenience (ease, speed, low friction), B7_Expert (technical detail, precision, comparison). Choose at most TWO secondary motivations. Report confidence 0.0-1.0 (0.90-1.00 = directly visible, 0.70-0.89 = strong inference, 0.50-0.69 = plausible, below 0.50 = unknown). Use motivation to weight role selection: B1 emphasize benefit/usage/feature-detail; B2 emphasize feature-detail/material-detail/scale, reduce lifestyle; B3 emphasize lifestyle/usage/benefit; B4 emphasize hero/alternate-view/material-detail; B5 emphasize package-contents/benefit, reserve overlay; B6 emphasize usage/package-contents/scale; B7 emphasize feature-detail/material-detail/scale, minimize lifestyle.',
        ...(guidanceArm === 'original' ? [marketContextGuidance] : []),
        `LANGUAGE AND COPY CONTRACT:\nOutput locale: ${state.locale || getLocaleFromPlatform(state.platform)}\nInstruction language arm: ${state.instructionLanguage === 'localized' ? 'localized' : 'en'}\nUse the output locale for visible short descriptions, slogans, badges, and labels. Never translate, paraphrase, transliterate, or invent copy. Operator-approved copy must remain exact. Keep visual instruction language in English unless instructionLanguage is explicitly set to the localized benchmark arm. Render each returned copy string exactly once when model-rendered; for overlay copy, reserve the named clean area and render no text.`,
        `Approved copy items for this batch: ${JSON.stringify(localizeCopyItems(state.copyItems, state.locale || getLocaleFromPlatform(state.platform)).map(({ id, kind, text, locale, location, render }) => ({ id, kind, text, locale, location, render })))}. ${copyPolicy.allowShaperAuthoredCopy ? 'If this list is empty, follow PLATFORM IMAGE-COPY STRATEGY to author factual copy.' : 'Do not create copy items when this list is empty.'}`,
        copyStrategyGuidance,
        ...(guidanceArm === 'revised' ? [revisedMarketContextGuidance, visualSelectionGuidance] : []),
        'Only use usage contexts supported by supplied product facts. Depict people only when operator input supports the audience; do not infer children or safety claims from season.',
        'Evidence boundary for every slot: product photos establish visible appearance, but adjacent objects do not prove in-box contents. Name an accessory as included only when supplied facts explicitly confirm it. Do not add documentation, bundle items, performance claims, ruggedness, stabilization, use timing, or precision claims by inference from product category or visual style. Do not assert a national or platform audience preference without supplied or tested evidence. When facts are insufficient for a package-contents role, choose another useful role grounded in the verified product instead. These limits do not prescribe backgrounds, colors, lighting, composition, or scene details that remain Open.',
        'When category creative preference or batch direction seeds a scene concept, build around it and set sceneSource to operator; otherwise use shaper.',
        `Platform: ${template.name}; aspect ratio: ${template.aspectRatio}; image-count bounds: ${template.minImageCount}-${template.maxImageCount}; default: ${template.imageCount}; hard rules: ${template.slotRules.join(' | ')}`,
        'Return exactly resolvedImageCount slot objects, with indexes 1 through resolvedImageCount and no missing or duplicate indexes.',
        `Category guidance: ${CATEGORY_PRESETS[state.category]?.guidance || ''}`,
        `Product: ${state.productName}; variant: ${state.productVariant}; facts: ${state.categoryFacts}`,
        `Campaign season (target, paired with market ${template.name}): ${state.season || 'unspecified'}; promotion: ${state.promotion || 'none'}; batch direction: ${state.batchDirection || 'none'}`,
        `Operator constraints: ${JSON.stringify(constraints)}`,
        `Reference image roles: ${JSON.stringify(state.referenceImages.map(image => image.roles || []))}`,
        state.imageCountTouched ? `Operator fixed image count: ${state.imageCount}` : 'Operator has not fixed image count; propose a count within the platform bounds.',
        'Author one shared batchTone object for all slots. Every differentiator must be distinct and explicitly distinguish its slot from siblings.'
    ].join('\n\n');
    const parts = [{ text: prompt }];
    assets.productImages.forEach((image, index) => {
        parts.push({ text: `Product image ${index + 1}; inspect it for productRead.` });
        parts.push({ inlineData: { mimeType: (image.data.match(/^data:([^;]+);/) || [])[1] || 'image/png', data: (image.data.split(',')[1] || image.data) } });
    });
    assets.referenceImages.forEach((image, index) => {
        parts.push({ text: `Reference image ${index + 1}; roles: ${(image.roles || []).join(', ') || 'general inspiration'}.` });
        parts.push({ inlineData: { mimeType: (image.data.match(/^data:([^;]+);/) || [])[1] || 'image/png', data: (image.data.split(',')[1] || image.data) } });
    });
    const responseSchema = {
        type: 'OBJECT',
        required: ['productRead', 'batchTone', 'resolvedImageCount', 'countRationale', 'slots'],
        properties: {
            planFormatVersion: { type: 'STRING', enum: [PLAN_FORMAT_VERSION] },
            id: { type: 'STRING' }, planSource: { type: 'STRING', enum: ['shaper'] },
            shapedAt: { type: 'STRING' }, model: { type: 'STRING', enum: [SHAPER_MODEL_ID] },
            productRead: { type: 'OBJECT', required: ['verificationNeed', 'purchaseType', 'infoLocation', 'anglesSupplied', 'notes', 'buyerMotivation'], properties: {
                verificationNeed: { type: 'STRING', enum: ['low', 'medium', 'high'] },
                purchaseType: { type: 'STRING', enum: ['repeat', 'one-off'] },
                infoLocation: { type: 'STRING', enum: ['packaging', 'listing', 'both'] },
                anglesSupplied: { type: 'INTEGER' }, notes: { type: 'STRING' },
                buyerMotivation: { type: 'OBJECT', required: ['primary', 'confidence'], properties: {
                    primary: { type: 'STRING', enum: ['B1_Functional', 'B2_Evidence', 'B3_Lifestyle', 'B4_Aesthetic', 'B5_Value', 'B6_Convenience', 'B7_Expert'] },
                    secondary: { type: 'ARRAY', items: { type: 'STRING', enum: ['B1_Functional', 'B2_Evidence', 'B3_Lifestyle', 'B4_Aesthetic', 'B5_Value', 'B6_Convenience', 'B7_Expert'] } },
                    confidence: { type: 'NUMBER', minimum: 0, maximum: 1 },
                    reason: { type: 'STRING' }
                } }
            } },
            batchTone: { type: 'OBJECT', required: ['character', 'palette', 'mood', 'finish'], properties: {
                character: { type: 'STRING' }, palette: { type: 'STRING' }, mood: { type: 'STRING' }, finish: { type: 'STRING' }
            } },
            resolvedImageCount: { type: 'INTEGER', minimum: template.minImageCount, maximum: template.maxImageCount },
            countRationale: { type: 'STRING' },
            slots: { type: 'ARRAY', items: { type: 'OBJECT', required: ['index', 'role', 'direction', 'differentiator', 'sceneRationale', 'sceneSource', 'copyPlacement', 'outputLocale', 'instructionLanguage', 'copyItems', 'derivedFrom', 'visualElements'], properties: {
                index: { type: 'INTEGER' }, role: { type: 'STRING', enum: Array.from(SHAPER_ROLES) },
                direction: { type: 'STRING' }, differentiator: { type: 'STRING' }, sceneRationale: { type: 'STRING' },
                sceneSource: { type: 'STRING', enum: ['shaper', 'operator'] },
                copyPlacement: { type: 'STRING', enum: ['none', 'model-rendered', 'reserve-overlay-area'] },
                outputLocale: { type: 'STRING' },
                instructionLanguage: { type: 'STRING', enum: ['en', 'localized'] },
                copyItems: { type: 'ARRAY', items: { type: 'OBJECT', required: ['id', 'kind', 'text', 'locale', 'location', 'render'], properties: {
                    id: { type: 'STRING' },
                    kind: { type: 'STRING', enum: ['slogan', 'short-description', 'badge', 'label'] },
                    text: { type: 'STRING' },
                    locale: { type: 'STRING' },
                    location: { type: 'STRING' },
                    render: { type: 'STRING', enum: ['overlay', 'model-rendered'] }
                } } },
                derivedFrom: { type: 'STRING', enum: ['open', 'platform-rule', 'operator'] },
                visualElements: { type: 'OBJECT', required: ['backgroundType', 'productTreatment', 'layoutComposition', 'colorPalette', 'lifestyleLevel', 'textStrategy'], properties: {
                    backgroundType: { type: 'STRING', enum: ['pure-white', 'neutral-solid', 'gradient', 'contextual-scene', 'lifestyle-environment'] },
                    productTreatment: { type: 'STRING', enum: ['centered-isolated', 'angled-with-shadow', 'in-context', 'in-use'] },
                    layoutComposition: { type: 'STRING', enum: ['product-dominant', 'balanced', 'environmental'] },
                    colorPalette: { type: 'STRING', enum: ['product-accurate', 'warm-enhanced', 'cool-enhanced', 'vibrant-pop'] },
                    lifestyleLevel: { type: 'STRING', enum: ['none', 'subtle-props', 'full-scene', 'human-presence'] },
                    textStrategy: { type: 'STRING', enum: ['text-free', 'reserve-overlay-space', 'model-rendered-headline'] }
                } }
            } } }
        }
    };
    responseSchema.required.push('planFormatVersion', 'id', 'planSource', 'shapedAt', 'model');
    return { contents: [{ role: 'user', parts }], generationConfig: { responseMimeType: 'application/json', responseSchema, responseModalities: ['TEXT'], maxOutputTokens: 12288 } };
}

function describeReferences(referenceImages) {
    if (referenceImages.length === 0) return [];

    return referenceImages.map((image, index) => {
        const roles = image.roles?.length ? image.roles : ['general inspiration'];
        const directions = [];
        if (roles.includes('style')) directions.push('carry over its design tone and mood, not its exact design');
        if (roles.includes('layout')) directions.push('use its spatial principles as guidance without copying the layout');
        if (roles.includes('color')) directions.push('use its palette and color relationships as guidance');
        if (roles.includes('lighting')) directions.push('match its lighting direction and quality, adapting the setup to the product');
        if (roles.includes('composition')) directions.push('adopt its camera angle and framing approach while keeping the product clearly visible');
        if (roles.includes('context')) directions.push('create a similar environmental setting and background treatment');
        if (directions.length === 0) directions.push('use it only as broad inspiration');

        return {
            assetId: image.id || `reference-${index + 1}`,
            name: image.name,
            roles,
            instruction: directions.join('; ')
        };
    });
}

function buildVisualElementInstruction(visualElements, outputLocale) {
    const visual = visualElements || {};
    return `VISUAL ELEMENT PLAN\nBackground category: ${visual.backgroundType || 'model choice'}\nProduct treatment: ${visual.productTreatment || 'model choice'}\nLayout composition: ${visual.layoutComposition || 'model choice'}\nColor palette category: ${visual.colorPalette || 'model choice'}\nLifestyle level: ${visual.lifestyleLevel || 'model choice'}\nText strategy: ${visual.textStrategy || 'text-free'}\nUse these as slot-level guidance for the ${outputLocale} output. Preserve product truth and platform rules. Choose the exact colors, materials, lighting, props, camera angle, spacing, and composition yourself when they are not supplied facts or operator constraints. Do not force this slot's treatment onto sibling slots.`;
}

function buildLanguageContract(outputLocale, instructionLanguage) {
    const normalizedLocale = outputLocale || 'en-US';
    const localized = instructionLanguage === 'localized';
    const languageName = normalizedLocale === 'zh-TW'
        ? 'Traditional Chinese (繁體中文)'
        : normalizedLocale === 'ja-JP'
            ? 'Japanese (日本語)'
            : normalizedLocale;
    return `LANGUAGE AND COPY CONTRACT\nOutput locale: ${normalizedLocale} (${languageName})\nInstruction language: ${localized ? languageName : 'English'}\nUse the output locale for any visible slogans, labels, badges, or other rendered copy. Keep product names, trademarks, and supplied text exactly as provided. Do not translate, paraphrase, transliterate, or invent copy. ${localized ? `Write any model-authored visible copy in ${languageName}.` : 'Keep visual instructions in English; this does not change the output locale.'}`;
}

function buildPromptRecord(imageIndex) {
    const template = PLATFORM_TEMPLATES[state.platform];
    const category = CATEGORY_PRESETS[state.category];
    const copyPolicy = getPlatformCopyPolicy(state.platform);
    const plan = state.shaperPlan || (state.shaperPlan = buildFallbackPlan(template));
    const slot = plan.slots[imageIndex] || buildFallbackPlan(template).slots[imageIndex];
    const purpose = slot?.role || roleForPurpose(template.imagePurposes[imageIndex], imageIndex);
    const platformRule = template.slotRules[imageIndex]
        || 'Create a useful additional product view with a new composition that fits the shared batch tone.';
    const constraints = getActiveConstraints(template);
    const assets = getSelectedAssets();
    const referenceRelationships = describeReferences(assets.referenceImages);
    const outputLocale = slot?.outputLocale || state.locale || getLocaleFromPlatform(state.platform);
    const planCopyItems = plan.planSource === 'shaper' && Array.isArray(slot?.copyItems) ? slot.copyItems : [];
    const copyPlan = planCopyItems.length > 0
        ? {
            source: 'shaper',
            modelRenderedText: planCopyItems.filter(item => item.render === 'model-rendered').map(item => ({ label: `${item.id} (${item.kind})`, value: item.text, location: item.location })),
            overlayText: planCopyItems.filter(item => item.render === 'overlay').map(item => ({ label: `${item.id} (${item.kind}) at ${item.location}`, value: item.text }))
        }
        : plan.planSource === 'shaper'
            ? { source: 'shaper', modelRenderedText: [], overlayText: [] }
            : buildCopyPlan(constraints, imageIndex);
    const mustHave = constraints.filter(item => item.level === 'must-have');
    const preferred = constraints.filter(item => item.level === 'preferred');
    const isAmazonMain = state.platform === 'amazon-jp' && imageIndex === 0;

    const sections = [
        `Create a new ${template.name} eCommerce product photograph using the attached product photos as identity references.`,
        `SLOT ${imageIndex + 1} OF ${state.imageCount}\nGenerate only this slot's one image. Do not generate the other slots.\nPurpose: ${purpose}\nShaper direction: ${slot?.direction || ''}\nDifferentiator: ${slot?.differentiator || ''}\nScene rationale: ${slot?.sceneRationale || ''}`,
        `PLATFORM AND SLOT REQUIREMENTS\n${platformRule}\nIf Shaper direction conflicts with this platform rule, the platform rule wins.\nShared tone: ${JSON.stringify(plan.batchTone || template.tone)}`,
        `PLATFORM IMAGE-COPY STRATEGY (${copyPolicy.mode})\n${copyPolicy.guidance}\nRender only copy explicitly supplied in the text sections below. Keep any text concise, legible, and in ${outputLocale}. Do not invent prices, ratings, safety instructions, disclaimers, or unsupported claims.`,
        buildLanguageContract(outputLocale, slot?.instructionLanguage || state.instructionLanguage || 'en'),
        buildVisualElementInstruction(slot?.visualElements, outputLocale),
        `PRODUCT IDENTITY - MUST PRESERVE\nProduct name: ${state.productName.trim()}\nVariant: ${state.productVariant.trim() || 'Use the exact variant shown in the product photos.'}\nTreat every attached product photo as another view of the same product. Preserve its geometry, proportions, colors, materials, packaging, visible labels, logos, quantity, and included components. Do not redesign or replace the product.\nCategory guardrail: ${category.guidance}`
    ];

    if (state.categoryFacts.trim()) {
        sections.push(`ADDITIONAL PRODUCT FACTS - MUST PRESERVE\n${state.categoryFacts.trim()}`);
    }

    if (mustHave.length > 0) {
        sections.push(`MUST HAVE\n${mustHave.map(item => `${item.label}: ${item.value}`).join('\n')}\nUse these as exact factual direction. Do not typeset them unless the text instructions below explicitly request it.`);
    }

    const preferences = [
        ...preferred.map(item => `${item.label}: ${item.value}`),
        state.categoryPreference.trim() ? `Category creative preference: ${state.categoryPreference.trim()}` : '',
        state.batchDirection.trim() ? `Batch direction: ${state.batchDirection.trim()}` : ''
    ].filter(Boolean);
    if (preferences.length > 0) {
        sections.push(`PREFERRED DIRECTION\nFollow these when they support a strong image, but product truth and platform requirements take priority.\n${preferences.join('\n')}`);
    }

    if (referenceRelationships.length > 0) {
        sections.push(`VISUAL REFERENCES\n${referenceRelationships.map((reference, index) => `Reference ${index + 1} (${reference.name}): ${reference.instruction}.`).join('\n')}\nMaintain a related design tone while creating an original scene and composition for this product.`);
    }

    if (state.imageCount > 1) {
        const siblingSlots = plan.slots
            .filter((_, index) => index !== imageIndex)
            .map(sibling => `- ${sibling.role}: ${sibling.differentiator}`)
            .join('\n');
        sections.push(`AVOID DUPLICATING SIBLING SLOTS\nThis batch contains ${state.imageCount} distinct images. Do not repeat or closely mimic the composition, angle, or concept planned for these sibling slots:\n${siblingSlots}\n\nYour differentiator for this slot: "${slot.differentiator}"`);
    }

    if (copyPlan.modelRenderedText.length > 0) {
        if (copyPlan.source === 'shaper') {
            sections.push(`MODEL-RENDERED TEXT\nRender only these exact approved strings in ${outputLocale}, once each, at the specified locations. Do not translate, paraphrase, transliterate, or add other text.\n${copyPlan.modelRenderedText.map(item => `${item.label} at ${item.location}: "${item.value}"`).join('\n')}`);
        } else {
            sections.push(`MODEL-RENDERED TEXT\nRender only this supplied short headline, exactly as quoted: "${copyPlan.modelRenderedText[0].value}". Make it legible and appropriate to the batch tone. Do not add other promotional wording.`);
        }
    }

    if (copyPlan.overlayText.length > 0) {
        sections.push(`TEXT TO ADD AFTER GENERATION\nDo not render the following exact copy in the image. Leave one clean, uncluttered area with enough contrast for a later text overlay.\n${copyPlan.overlayText.map(item => `${item.label}: ${item.value}`).join('\n')}`);
    } else if (!isAmazonMain && copyPlan.modelRenderedText.length === 0) {
        sections.push('TEXT HANDLING\nDo not invent or render prices, ratings, specifications, claims, badges, warnings, or promotional copy.');
    }

    sections.push('CREATIVE FREEDOM\nChoose an original commercially useful composition, camera angle, lighting, props, and scene for every detail not constrained above. Keep this batch in one design tone, but do not repeat or copy the same design.');
    sections.push('ACCURACY\nUse only supplied facts. Do not invent measurements, ingredients, certifications, ratings, discounts, comparisons, accessories, or product capabilities. The final image must look like real, polished product photography.');
    sections.push('OUTPUT CONTRACT\nReturn exactly one final image for this slot. Do not create a collage, contact sheet, or alternate variation.');

    return {
        id: generateId('prompt'),
        index: imageIndex + 1,
        purpose,
        promptFormatVersion: PROMPT_FORMAT_VERSION,
        platform: state.platform,
        category: state.category,
        outputLocale,
        instructionLanguage: slot?.instructionLanguage || state.instructionLanguage || 'en',
        copyItems: slot?.copyItems || [],
        shaperPlanId: plan.id,
        planSource: plan.planSource,
        aspectRatio: template.aspectRatio,
        constraints,
        productAssets: assets.productImages.map((image, index) => ({
            assetId: image.id || `product-${index + 1}`,
            name: image.name
        })),
        referenceRelationships,
        copyPlan,
        visualElements: slot?.visualElements || null,
        prompt: sections.join('\n\n')
    };
}

function buildPrompt(imageIndex) {
    return buildPromptRecord(imageIndex).prompt;
}

function compilePromptRecords() {
    return Array.from(
        { length: state.imageCount },
        (_, index) => buildPromptRecord(index)
    );
}

function buildBatchPromptRecord(promptRecords) {
    if (!promptRecords.length) throw new Error('Cannot build a batch prompt without prompt records.');

    const splitSections = record => record.prompt.split(/\n\n+/);
    const firstSections = splitSections(promptRecords[0]);
    const sharedTone = firstSections
        .find(section => section.startsWith('PLATFORM AND SLOT REQUIREMENTS'))
        ?.split('\n')
        .find(line => line.startsWith('Shared tone:'));
    const isSlotSection = section => (
        section.startsWith('SLOT ')
        || section.startsWith('PLATFORM AND SLOT REQUIREMENTS')
        || section.startsWith('VISUAL ELEMENT PLAN')
        || section.startsWith('MODEL-RENDERED TEXT')
        || section.startsWith('TEXT TO ADD AFTER GENERATION')
        || section.startsWith('TEXT HANDLING')
        || section.startsWith('AVOID DUPLICATING SIBLING SLOTS')
        || section.startsWith('OUTPUT CONTRACT')
    );
    const sharedSections = firstSections.filter(section => !isSlotSection(section));
    if (sharedTone) sharedSections.splice(1, 0, sharedTone);

    const slotBlocks = promptRecords.map(record => {
        const sections = splitSections(record);
        const imageSection = sections.find(section => section.startsWith('SLOT '));
        const platformSection = sections.find(section => section.startsWith('PLATFORM AND SLOT REQUIREMENTS'));
        const visualSection = sections.find(section => section.startsWith('VISUAL ELEMENT PLAN'));
        const slotRequirements = platformSection
            ?.split('\n')
            .filter(line => !line.startsWith('Shared tone:'))
            .join('\n');
        const textSections = sections.filter(section => (
            section.startsWith('MODEL-RENDERED TEXT')
            || section.startsWith('TEXT TO ADD AFTER GENERATION')
            || section.startsWith('TEXT HANDLING')
            || section.startsWith('AVOID DUPLICATING SIBLING SLOTS')
        ));

        return [
            `SLOT ${record.index}`,
            imageSection,
            slotRequirements,
            visualSection,
            ...textSections
        ].filter(Boolean).join('\n\n');
    });
    const count = promptRecords.length;
    const outputContract = [
        'OUTPUT CONTRACT',
        `Return exactly ${count} separate images, one for each numbered slot.`,
        'Return the images in slot order.',
        'For each slot, emit one separate image part; never combine slots into a collage.',
        'Keep one shared design tone across the batch, but use a distinct composition for every slot and do not repeat a composition.'
    ].join('\n');

    return {
        promptIds: promptRecords.map(record => record.id),
        aspectRatio: promptRecords[0].aspectRatio,
        referenceRelationships: promptRecords[0].referenceRelationships,
        prompt: [
            'SHARED BATCH CONTEXT',
            ...sharedSections,
            'NUMBERED IMAGE SLOTS',
            ...slotBlocks,
            outputContract
        ].join('\n\n')
    };
}

function buildImagePayload(promptRecord, assets) {
        const toInlineData = (dataUrl) => {
            const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
            if (!match) throw new Error('An uploaded image has an unsupported format.');

            return {
                inlineData: {
                    mimeType: match[1],
                    data: match[2]
                }
            };
        };

        const parts = [{ text: promptRecord.prompt }];

        assets.productImages.forEach((image, index) => {
            parts.push(
                { text: `Product identity image ${index + 1}. This is the same product from another view; preserve its identity and visible details.` },
                toInlineData(image.data)
            );
        });

        assets.referenceImages.forEach((image, index) => {
            const relationship = promptRecord.referenceRelationships[index];
            parts.push(
                { text: `Visual reference ${index + 1}: ${relationship.instruction}.` },
                toInlineData(image.data)
            );
        });

        // Vertex AI generateContent shape. The server adds project, location and
        // the ADC bearer token, so the model name is not part of the body.
        return {
            contents: [{ role: 'user', parts }],
            generationConfig: {
                responseModalities: ['TEXT', 'IMAGE'],
                maxOutputTokens: 32768,
                imageConfig: {
                    aspectRatio: promptRecord.aspectRatio,
                    imageSize: IMAGE_SIZE
                }
            }
        };
}


 return { buildFallbackPlan, validateRawShaperPlan, validateShaperPlan, buildShaperPayload, buildPromptRecord, compilePromptRecords, buildBatchPromptRecord, buildImagePayload, getSelectedAssets };
}
export { PLATFORM_TEMPLATES, CATEGORY_PRESETS };
